<template>
  <div>

    <company-form
      v-if="isAddNewCompanySidebarActive"
      :is-add-new-company-sidebar-active.sync="isAddNewCompanySidebarActive"
      :company="company"
      @form-submitted="formSubmitted"
    />

    <b-card
      no-body
      class="mb-0"
    >
      <div class="m-2">
        <b-row>
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label>Show</label>

            <v-select
              v-model="pageSize"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
            />

            <label>entries</label>
          </b-col>

          <b-col
            cols="12"
            md="6"
          >
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                v-model="searchQuery"
                class="d-inline-block mr-1"
                placeholder="Press ENTER to search"
                @keyup.enter="refCompaniesTable.refresh();"
              />

              <b-button
                variant="primary"
                @click="openSidebar"
              >
                <span class="text-nowrap">Add Company</span>
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        id="companies-table"
        ref="refCompaniesTable"
        class="position-relative"
        :items="getCompaniesList"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="orderBy"
        show-empty
        empty-text="No matching records found"
        :sort-desc.sync="orderDesc"
        style="min-height:110px"
      >
        <!-- Column: Name -->
        <template #cell(name)="data">
          <b-link
            :to="{ name: 'company-details', params: { id: data.item.id } }"
            class="font-weight-bold d-block text-nowrap"
          >
            {{ data.item.name }}
          </b-link>
        </template>

        <!-- Column: Email -->
        <template #cell(email)="data">
          {{ data.item.email }}
        </template>

        <template #cell(contactTel)="data">
          {{ data.item.contactTel }}
        </template>

        <!-- Column: Pays Admin Fee -->
        <template #cell(paysAdminFee)="data">
          <b-badge
            pill
            :variant="(data.item.paysAdminFee) ? 'light-success' : 'light-secondary'"
            class="text-capitalize"
          >
            {{ (data.item.paysAdminFee) ? 'Yes' : 'No' }}
          </b-badge>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-dropdown
            variant="link"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>

            <b-dropdown-item :to="{ name: 'company-details', params: { id: data.item.id } }">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50">Manage</span>
            </b-dropdown-item>

            <b-dropdown-item @click="editCompany(data.item)">
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">Edit</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">Showing {{ dataMeta.from }} to {{ dataMeta.to }} of {{ dataMeta.of }}
              entries</span>
          </b-col>

          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="pageNumber"
              :total-rows="totalCompanies"
              :per-page="pageSize"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import { BBadge, BButton, BCard, BCol, BDropdown, BDropdownItem, BFormInput, BLink, BPagination, BRow, BTable } from 'bootstrap-vue';
import { ref } from '@vue/composition-api';
import CompanyForm from '@/modules/company/components/CompanyForm.vue';
import useCompaniesList from '@/modules/company/composables/useCompanyList';
import useCompany from '@/modules/company//composables/useCompany';
import vSelect from 'vue-select';
import Swal from 'sweetalert2';

export default {
  setup() {
    const { deleteCompany } = useCompany();
    const {
      dataMeta,
      getCompaniesList,
      orderBy,
      orderDesc,
      pageNumber,
      pageSize,
      perPageOptions,
      refCompaniesTable,
      searchQuery,
      tableColumns,
      totalCompanies,
    } = useCompaniesList();

    const emptyCompany = { id: null, name: null, paysAdminFee: null, isGha: false, isGibAir: false, isCancelled: false, email: null, contactTel: null };

    const isAddNewCompanySidebarActive = ref(false);
    const company = ref({ ...emptyCompany });

    const removeCompany = async (company) => {
      const { value } = await Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      });

      if (!value) return;

      await deleteCompany(company);

      refCompaniesTable.value.refresh();
    };

    const editCompany = (companySelected) => {
      company.value = { ...companySelected };
      isAddNewCompanySidebarActive.value = true;
    };

    const formSubmitted = () => {
      isAddNewCompanySidebarActive.value = false;
      company.value = { ...emptyCompany };
      refCompaniesTable.value.refresh();
    };

    const openSidebar = () => {
      company.value = { ...emptyCompany };
      isAddNewCompanySidebarActive.value = true;
    };

    return {
      // Reactive
      company,
      isAddNewCompanySidebarActive,

      //Method
      removeCompany,
      editCompany,
      formSubmitted,
      openSidebar,

      // UseCompaniesList
      dataMeta,
      getCompaniesList,
      orderBy,
      orderDesc,
      pageNumber,
      pageSize,
      perPageOptions,
      refCompaniesTable,
      searchQuery,
      tableColumns,
      totalCompanies,
    };
  },
  components: {
    BBadge,
    BButton,
    BCard,
    BCol,
    BDropdown,
    BDropdownItem,
    BFormInput,
    BLink,
    BPagination,
    BRow,
    BTable,
    CompanyForm,
    vSelect,
  }
};
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

#companies-table {
  min-height: 150px;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>